
import { db } from '../lib/firebase';
import { collection, query, where, getDocs, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';

const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
const SMS_CONFIG = {
  baseUrl: isProduction ? 'https://api.allorigins.win/get?url=' : '/smsAPI',
  from: process.env.REACT_APP_SMS_SENDER_ID || 'SENDERID',
};

/**
 * Generic fetch helper to handle proxy differences between Dev and Production
 */
const fetchAPI = async (params) => {
  try {
    let finalUrl = `${SMS_CONFIG.baseUrl}?${params.toString()}`;
    if (isProduction) {
      const target = `https://sms.ravanahost.com/smsAPI?${params.toString()}`;
      finalUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(target)}`;
    }

    const response = await fetch(finalUrl, { method: 'GET' });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    
    const wrapper = await response.json();
    // AllOrigins returns valid data in the 'contents' property
    const data = isProduction ? JSON.parse(wrapper.contents) : wrapper;
    return data;
  } catch (error) {
    console.error('Fetch API Error:', error);
    throw error;
  }
};

/**
 * Formats a phone number for the SMS API (likely Sri Lankan format 947XXXXXXXX)
 */
const formatPhoneNumber = (phone) => {
  let formatted = phone.replace(/\D/g, ''); // Remove non-digits
  if (formatted.startsWith('0')) {
    formatted = '94' + formatted.substring(1);
  } else if (!formatted.startsWith('94') && formatted.length === 9) {
    formatted = '94' + formatted;
  }
  return formatted;
};

/**
 * Core function to send SMS via Ravanahost API
 */
export const sendSMS = async (to, text, config = {}) => {
  const apikey = process.env.REACT_APP_SMS_API_KEY || config.apikey;
  const apitoken = process.env.REACT_APP_SMS_API_TOKEN || config.apitoken;
  const from = process.env.REACT_APP_SMS_SENDER_ID || config.from || SMS_CONFIG.from;
  
  if (!apikey || !apitoken) {
    console.error('SMS API Key or Token is missing');
    return { status: 'error', message: 'API Configuration Missing' };
  }

  const formattedTo = formatPhoneNumber(to);
  const params = new URLSearchParams({
    apikey: apikey,
    apitoken: apitoken,
    type: 'sms',
    from: from,
    to: formattedTo,
    text: text,
  });

  params.append('sendsms', '');
  if (config.scheduledate) {
    params.append('scheduledate', config.scheduledate);
  }

  console.log('--- SMS ATTEMPT (GET) ---');
  console.log('Target:', formattedTo);
  console.log('Sender:', from);

  try {
    const data = await fetchAPI(params);
    console.log('API Response:', data);
    return data;
  } catch (error) {
    return { status: 'error', message: error.message };
  }
};

/**
 * Check the status of a sent SMS group
 */
export const checkSMSStatus = async (groupId, config = {}) => {
  const apikey = process.env.REACT_APP_SMS_API_KEY || config.apikey;
  const apitoken = process.env.REACT_APP_SMS_API_TOKEN || config.apitoken;
  const params = new URLSearchParams({
    apikey: apikey,
    apitoken: apitoken,
    groupid: groupId
  });

  params.append('groupstatus', '');

  try {
    const data = await fetchAPI(params);
    return data;
  } catch (error) {
    console.error('Status Check Failed:', error);
    return { status: 'error', message: error.message };
  }
};

/**
 * Read SMS Inbox
 */
export const readSMSInbox = async (list = 'unread', config = {}) => {
  const apikey = process.env.REACT_APP_SMS_API_KEY || config.apikey;
  const apitoken = process.env.REACT_APP_SMS_API_TOKEN || config.apitoken;
  const params = new URLSearchParams({
    apikey: apikey,
    apitoken: apitoken,
    list: list
  });

  params.append('getinbox', '');

  try {
    const data = await fetchAPI(params);
    return data;
  } catch (error) {
    console.error('Inbox Fetch Failed:', error);
    return { status: 'error', message: error.message };
  }
};

/**
 * Check SMS Credit Balance
 */
export const checkBalance = async (config = {}) => {
  const apikey = process.env.REACT_APP_SMS_API_KEY || config.apikey;
  const apitoken = process.env.REACT_APP_SMS_API_TOKEN || config.apitoken;
  const params = new URLSearchParams({
    apikey: apikey,
    apitoken: apitoken
  });

  params.append('balance', '');

  try {
    const data = await fetchAPI(params);
    return data;
  } catch (error) {
    console.error('Balance Check Failed:', error);
    return null;
  }
};

/**
 * Sends SMS with a rate limit of 2 messages per hour per phone number.
 * Uses Firestore to track message counts.
 */
export const sendSMSWithRateLimit = async (to, text, config = {}) => {
  const formattedTo = formatPhoneNumber(to);
  const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
  
  try {
    // Check SMS tracking collection for recent sends to this number
    const trackingRef = collection(db, 'sms_tracking');
    const q = query(
      trackingRef, 
      where('to', '==', formattedTo)
    );
    
    const querySnapshot = await getDocs(q);
    const recentSends = querySnapshot.docs.filter(doc => {
      const ts = doc.data().timestamp;
      return ts && ts.toDate() >= oneHourAgo;
    });
    
    if (recentSends.length >= 2) {
      return { 
        status: 'error', 
        message: 'Rate limit exceeded. Only 2 SMS allowed per hour for this number.' 
      };
    }

    // Proceed to send SMS
    const result = await sendSMS(formattedTo, text, config);

    // If successfully queued/sent, log to Firestore
    if (result.status === 'queued' || result.status === 'success') {
      await addDoc(trackingRef, {
        to: formattedTo,
        timestamp: serverTimestamp(),
        type: config.type || 'verification',
        status: result.status,
        groupId: result.group_id || null
      });
    }

    return result;
  } catch (error) {
    console.error('Rate limited sending failed:', error);
    return { status: 'error', message: error.message || 'SMS service error' };
  }
};
